import { createDraftSafeSelector } from '@reduxjs/toolkit';

import type { RootState } from '@polyai/stream/store';

import { flowFunctionsSelectors, flowStepsSelectors } from '.';
import type { Flow } from './types/flow';
import { isAdvancedStep, isDefaultStep, isFunctionStep } from './types/steps';
import { flowsSelectors } from './utils';

const selectRootFlows = (state: RootState) => state.flows;

export const selectFlows = createDraftSafeSelector(
  [selectRootFlows],
  ({ flows }) => {
    return flowsSelectors.selectAll(flows);
  },
);

export const selectSteps = createDraftSafeSelector(
  [(flow: Flow | null | undefined) => flow],
  flow => {
    return flow ? flowStepsSelectors.selectAll(flow.steps) : [];
  },
);

// Advanced steps

export const selectAdvancedSteps = createDraftSafeSelector(
  [selectSteps],
  steps => steps.filter(isAdvancedStep),
);

export const selectAdvancedStepById = createDraftSafeSelector(
  [(flow: Flow | null | undefined, id: string) => ({ flow, id })],
  ({ flow, id }) => {
    if (!flow) {
      return undefined;
    }
    const step = flowStepsSelectors.selectById(flow.steps, id);
    if (!step) {
      return undefined;
    }
    return isAdvancedStep(step) ? step : undefined;
  },
);

// Default steps

export const selectDefaultSteps = createDraftSafeSelector(
  [selectSteps],
  steps => steps.filter(isDefaultStep),
);

export const selectDefaultStepById = createDraftSafeSelector(
  [(flow: Flow | null | undefined, id: string) => ({ flow, id })],
  ({ flow, id }) => {
    if (!flow) {
      return undefined;
    }
    const step = flowStepsSelectors.selectById(flow.steps, id);
    if (!step) {
      return undefined;
    }

    return isDefaultStep(step) ? step : undefined;
  },
);

// Function steps

export const selectFunctionSteps = createDraftSafeSelector(
  [selectSteps],
  steps => steps.filter(isFunctionStep),
);

// Transition functions (flow functions)

export const selectTransitionFunctions = createDraftSafeSelector(
  [selectFlows],
  flows => {
    // Get all transition functions from all flows
    return flows.flatMap(flow => {
      if (!flow.transitionFunctions) {
        return [];
      }
      const functions = flowFunctionsSelectors
        .selectAll(flow.transitionFunctions)
        .filter(func => !func.archived);
      // Add flow context to each function
      return functions.map(func => ({
        ...func,
        flow_id: flow.id,
        flow_name: flow.name,
      }));
    });
  },
);
